import React, { useEffect, useState } from "react";
import { decode } from "blurhash";

function hashToDataUrl(hash, width = 32, height = 32) {
  if (!hash) return "";
  try {
    const pixels = decode(hash, width, height, 1);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL("image/png");
  } catch {
    return "";
  }
}

export default function BlurHashImage({
  src,
  blurHash,
  alt = "",
  className = "",
  priority = false,
  ...props
}) {
  const [placeholder, setPlaceholder] = useState("");
  const [loadedSrc, setLoadedSrc] = useState("");
  const [failedSrc, setFailedSrc] = useState("");
  const [imageSrc, setImageSrc] = useState(src || "");
  const [triedOriginal, setTriedOriginal] = useState(false);
  const loaded = loadedSrc === imageSrc;
  const failed = failedSrc === imageSrc;

  useEffect(() => {
    setImageSrc(src || "");
    setTriedOriginal(false);
    setPlaceholder(blurHash ? hashToDataUrl(blurHash, 32, 32) : "");
  }, [src, blurHash]);

  const handleImageError = () => {
    // Retry the original image if thumbnail conversion fails.
    if (!triedOriginal && imageSrc.includes("variant=thumb")) {
      setTriedOriginal(true);
      const originalUrl = imageSrc
        .replace(/([?&])variant=thumb(&|$)/, (_, prefix, suffix) => {
          if (prefix === "?" && suffix === "&") return "?";
          if (prefix === "&" && suffix === "&") return "&";
          return "";
        })
        .replace(/[?&]$/, "");
      setImageSrc(originalUrl);
      return;
    }
    setFailedSrc(imageSrc);
  };

  if (!src) return null;

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#F3EDE2" }}>
      {failed ? (
        <div className="absolute inset-0 flex items-center justify-center p-3 text-center text-xs text-stone-500" role="img" aria-label={alt || "Image unavailable"}>
          Image unavailable
        </div>
      ) : null}
      {!failed && placeholder && (
        <img
          src={placeholder}
          alt=""
          aria-hidden="true"
          className={className + " absolute inset-0 w-full h-full object-cover scale-110"}
        />
      )}
      {!failed && <img
        key={imageSrc}
        src={imageSrc}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        onLoad={() => { setLoadedSrc(imageSrc); setFailedSrc(""); }}
        onError={handleImageError}
        className={className + " relative transition-opacity duration-300 " + (loaded ? "opacity-100" : "opacity-0")}
        {...props}
      />}
    </div>
  );
}
